<template>
    <div class="designer-detail-container">
        <div class="designer-header">
            <div class="designer-info">
                <el-image 
                    :src="designer.avatar" 
                    :alt="designer.name" 
                    class="designer-avatar"
                    fit="cover"
                >
                    <template #placeholder>
                        <div class="image-placeholder">
                            <el-icon class="is-loading"><Loading /></el-icon>
                        </div>
                    </template>
                    <template #error>
                        <div class="image-error">
                            <el-icon><PictureIcon /></el-icon>
                        </div>
                    </template>
                </el-image>
                <div class="designer-text">
                    <h1>{{ designer.name }}</h1>
                    <p class="designer-description">{{ designer.description }}</p>
                </div>
            </div>
        </div>
        <div class="content-section" v-for="(section, index) in designWorks" :key="index">
            <h2>{{ section.title }}</h2>
            <p class="section-desc">{{ section.description }}</p>
            
            <div class="equipment-list">
                <div class="equipment-item" v-for="(item, itemIndex) in section.items" :key="itemIndex">
                    <div class="equipment-item-content">
                        <div class="equipment-item-image">
                            <el-image 
                                :src="item.image"
                                :alt="item.title"
                                fit="cover"
                                :preview-src-list="[item.image]"
                                :initial-index="0"
                                preview-teleported
                                class="item-image"
                            >
                                <template #placeholder>
                                    <div class="image-placeholder">
                                        <el-icon class="is-loading"><Loading /></el-icon>
                                    </div>
                                </template>
                                <template #error>
                                    <div class="image-error">
                                        <el-icon><PictureIcon /></el-icon>
                                    </div>
                                </template>
                            </el-image>
                        </div>
                        <div class="equipment-item-info">
                            <h3>{{ item.title }}</h3>
                            <p class="item-specs">{{ item.specs }}</p>
                            <p class="item-price">价格：{{ item.price }}</p>
                            <p class="item-description">{{ item.description }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { fetchDesigners, fetchDesignerWorks } from '@/services/api'
import { Loading, Picture as PictureIcon } from '@element-plus/icons-vue'

export default {
    name: 'DesignerDetail',
    components: {
        Loading,
        PictureIcon
    },
    data() {
        return {
            designer: {
                name: '',
                avatar: '',
                description: ''
            },
            designWorks: []
        }
    },
    async created() {
        const designerId = this.$route.params.id;
        await this.fetchDesignerData(designerId);
    },
    methods: {
        async fetchDesignerData(id) {
            try {
                // 获取设计师基本信息
                const designers = await fetchDesigners();
                const designer = designers.find(d => d.id === id);
                if (designer) {
                    this.designer = designer;
                }

                // 获取设计师作品
                const works = await fetchDesignerWorks(id);
                
                // 按类型分组作品
                const groupedWorks = works.reduce((acc, work) => {
                    const type = work.specs.split(' / ')[1];
                    if (!acc[type]) {
                        acc[type] = {
                            title: `${work.specs.split(' / ')[0]}级设计`,
                            description: `包含${type}风格的设计作品`,
                            items: []
                        };
                    }
                    acc[type].items.push(work);
                    return acc;
                }, {});

                this.designWorks = Object.values(groupedWorks);
            } catch (error) {
                console.error('获取设计师数据失败:', error);
                // 这里可以添加错误提示
            }
        },
        viewDetails(item) {
            // 查看详情的处理逻辑
            console.log('查看详情:', item)
        }
    }
}
</script>

<style scoped>
.designer-detail-container {
    max-width: 1200px;
    width: 90%;
    margin: 0 auto;
    padding: 80px 0;
}

.designer-header {
    margin-bottom: 40px;
    padding: 30px;
    background: var(--adil-card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.designer-info {
    display: flex;
    align-items: center;
    gap: 20px;
}

.designer-avatar {
    width: 80px !important;
    height: 80px !important;
    border-radius: 50% !important;
    overflow: hidden !important;
    display: block !important;
}

:deep(.designer-avatar .el-image) {
    width: 100%;
    height: 100%;
    display: block;
}

:deep(.designer-avatar .el-image__inner) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}

:deep(.designer-avatar .image-placeholder),
:deep(.designer-avatar .image-error) {
    width: 100%;
    height: 100%;
    border-radius: 50%;
}

.designer-text h1 {
    margin: 0 0 10px;
    font-size: 24px;
    color: var(--adil-fontcolor);
}

.designer-description {
    margin: 0;
    color: var(--adil-secondtext);
    font-size: 14px;
    line-height: 1.6;
}

.content-section {
    margin-bottom: 40px;
    padding: 30px;
    background: var(--adil-card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.content-section h2 {
    margin: 0 0 10px;
    font-size: 20px;
    color: var(--adil-fontcolor);
}

.section-desc {
    margin: 0 0 20px;
    color: var(--adil-secondtext);
    font-size: 14px;
}

.equipment-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
}

.equipment-item {
    width: 100%;
    border: 1px solid rgba(0, 122, 255, 0.1);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;
    background: var(--adil-card-bg);
}

.equipment-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px -4px rgba(0, 122, 255, 0.15);
}

.equipment-item-content {
    display: flex;
    flex-direction: column;
}

.equipment-item-image {
    width: 100%;
    height: 200px;
    overflow: hidden;
    cursor: zoom-in;
}

.item-image {
    width: 100%;
    height: 100%;
}

:deep(.el-image) {
    width: 100%;
    height: 100%;
    display: block;
}

:deep(.el-image__inner) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.equipment-item:hover :deep(.el-image__inner) {
    transform: scale(1.05);
}

.equipment-item-info {
    padding: 20px;
}

.equipment-item-info h3 {
    margin: 0 0 8px;
    font-size: 18px;
    color: var(--adil-fontcolor);
}

.item-specs {
    margin: 0 0 12px;
    font-size: 14px;
    color: var(--adil-secondtext);
}

.item-price {
    margin: 0 0 12px;
    font-size: 16px;
    color: #f56c6c;
    font-weight: 500;
}

.item-description {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.6;
    color: var(--adil-secondtext);
}

.item-actions {
    display: flex;
    gap: 12px;
}

@media (max-width: 1200px) {
    .equipment-list {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .designer-detail-container {
        padding: 60px 0;
    }
    
    .equipment-list {
        grid-template-columns: 1fr;
    }
}

:deep(.el-image-viewer__wrapper) {
    z-index: 2100;
}

:deep(.el-image-viewer__close) {
    color: #fff;
}

.image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f5f7fa;
}

.image-placeholder .el-icon {
    font-size: 24px;
    color: #909399;
}

.image-error {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f5f7fa;
}

.image-error .el-icon {
    font-size: 24px;
    color: #909399;
}
</style> 